import React, { useState, useEffect } from 'react';
import { TaskFormData } from '../../services/taskService';
import { tagService, Tag } from '../../services/tagService';
import { projectService } from '../../services/projectService';
import { Project } from '../../types/Project';
import TagSelector from './TagSelector';
import './TaskForm.css';

interface TaskFormProps {
  initialData?: TaskFormData;
  onSubmit: (taskData: TaskFormData & { tag_names?: string[] }) => Promise<void>;
  onCancel: () => void;
  submitButtonText?: string;
  cancelButtonText?: string;
  showError?: boolean;
  errorMessage?: string | null;
  showSuccess?: boolean;
  successMessage?: string | null;
  isLoading?: boolean;
  projects?: Project[];
  userId?: number;
}

const TaskForm: React.FC<TaskFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitButtonText = 'Save Task',
  cancelButtonText = 'Cancel',
  showError = true,
  errorMessage,
  showSuccess = true,
  successMessage,
  isLoading = false,
  projects: propsProjects,
  userId
}) => {
  const [task, setTask] = useState<TaskFormData>(initialData || {
    title: '',
    description: '',
    status: 'todo',
    due_date: '',
    assigned_date: '',
    project_id: undefined
  });
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  useEffect(() => {
    loadAvailableTags();
    loadProjects();
  }, []);

  // Update task state when initialData changes
  useEffect(() => {
    if (initialData) {
      setTask(initialData);
    }
  }, [initialData]);

  const loadAvailableTags = async () => {
    try {
      const response = await tagService.getAllTags();
      if (response.success && response.tags) {
        setAvailableTags(response.tags);
      }
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  };

  const loadProjects = async () => {
    if (propsProjects) {
      setProjects(propsProjects);
      return;
    }
    
    if (!userId) {
      return;
    }

    try {
      setProjectsLoading(true);
      const response = await projectService.getUserProjects({
        user_id: userId.toString()
      });
      if (response.success && response.projects) {
        setProjects(response.projects.map(p => projectService.convertDatabaseProject(p)));
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task.title.trim()) return;

    const taskData = {
      ...task,
      tag_names: selectedTags.length > 0 ? selectedTags : undefined
    };
    
    await onSubmit(taskData);
  };

  return (
    <form className="task-form" onSubmit={handleSubmit}>
      {showError && errorMessage && (
        <div className="task-form-error">
          <span>{errorMessage}</span>
        </div>
      )}

      {showSuccess && successMessage && (
        <div className="task-form-success">
          <span>{successMessage}</span>
        </div>
      )}

      <input
        type="text"
        placeholder="Task title..."
        value={task.title}
        onChange={(e) => setTask({ ...task, title: e.target.value })}
        className="task-form-title-input"
        required
        autoFocus
      />
      <textarea
        placeholder="Description (optional)..."
        value={task.description || ''}
        onChange={(e) => setTask({ ...task, description: e.target.value })}
        className="task-form-description-input"
        rows={3}
      />
      <div className="task-form-row">
        <div className="task-form-date-group">
          <label htmlFor="task-form-due-date" className="task-form-date-label">Due Date</label>
          <input
            id="task-form-due-date"
            type="date"
            value={task.due_date || ''}
            onChange={(e) => setTask({ ...task, due_date: e.target.value })}
            className="task-form-date-input"
          />
        </div>
        <div className="task-form-date-group">
          <label htmlFor="task-form-assigned-date" className="task-form-date-label">Assigned Date</label>
          <input
            id="task-form-assigned-date"
            type="date"
            value={task.assigned_date || ''}
            onChange={(e) => setTask({ ...task, assigned_date: e.target.value })}
            className="task-form-date-input"
          />
        </div>
      </div>
      <div className="task-form-row">
        <select
          value={task.status || 'todo'}
          onChange={(e) => setTask({ ...task, status: e.target.value as any })}
          className="task-form-status-select"
        >
          <option value="todo">To Do</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
      </div>
      {projects.length > 0 && (
        <div className="task-form-row">
          <label htmlFor="task-form-project" className="task-form-date-label">Project (optional)</label>
          <select
            id="task-form-project"
            value={task.project_id || ''}
            onChange={(e) => setTask({ ...task, project_id: e.target.value ? Number(e.target.value) : undefined })}
            className="task-form-status-select"
          >
            <option value="">No Project</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="task-form-row">
        <TagSelector
          selectedTags={selectedTags}
          onChange={setSelectedTags}
          availableTags={availableTags}
          placeholder="Add tags..."
        />
      </div>
      <div className="task-form-actions">
        <button 
          type="submit" 
          className="task-form-save-btn" 
          disabled={!task.title.trim() || isLoading}
        >
          {isLoading ? 'Saving...' : submitButtonText}
        </button>
        <button 
          type="button"
          onClick={onCancel} 
          className="task-form-cancel-btn"
          disabled={isLoading}
        >
          {cancelButtonText}
        </button>
      </div>
    </form>
  );
};

export default TaskForm;

